// Vendor
import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { SPOT_CORAL } from '@thredup/styleguide/src/variables/colors';

// Internal
import { IconArrow } from '@src/svg/IconArrow';
import { Image } from '@src/components/Image/Image';
import { DEFAULT_SAL_PROPS } from '@src/config/sal';

// Styles
import styles from './styles.module.css';

const getBrands = graphql`
  query getBrands {
    allBestResaleBrandsJson {
      nodes {
        link
        logo {
          alt
          fileName
          maxWidth
        }
      }
    }
  }
`;

const BestResaleValue: React.FC = () => {
  // Hooks
  const data = useStaticQuery<GatsbyTypes.getBrandsQuery>(getBrands);
  const brands = data.allBestResaleBrandsJson.nodes;

  // Markup
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderBrand = (item: any, index: number) => {
    const { link, logo } = item;
    const { alt, fileName, maxWidth } = logo;
    const delay = index * 50;

    return (
      <a
        {...DEFAULT_SAL_PROPS}
        data-sal-delay={delay}
        className={styles.brand}
        key={`${alt}-${index}`}
        href={link}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className={styles.rank}>#{index + 1}</span>
        <Image
          alt={alt}
          className={styles.logo}
          fileName={fileName}
          maxWidth={maxWidth}
        />
      </a>
    );
  };

  return (
    <div className="tup-slide">
      <h1 {...DEFAULT_SAL_PROPS} className="max-w-xl">
        Top 10 Brands With the Best Overall Resale Value
      </h1>
      <p {...DEFAULT_SAL_PROPS} className="tup-body">
        Two sustainable brands make the cut, while a fast fashion fave takes a
        hit.
      </p>
      <div className="flex flex-wrap justify-around mt-4">
        {brands.map(renderBrand)}
      </div>
      <div className="flex flex-wrap justify-center mt-12">
        <div
          {...DEFAULT_SAL_PROPS}
          data-sal-delay={500}
          className="ml-4 mr-4 flex mb-4"
        >
          <IconArrow className="mr-2 mt-1" />
          <p>
            <strong>FRYE is #1</strong>
            <br />
            FRYE keeps top spot for 2nd
            <br /> year running.
          </p>
        </div>
        <div
          {...DEFAULT_SAL_PROPS}
          data-sal-delay={550}
          className="ml-4 mr-4 flex mb-4"
        >
          <IconArrow className="mr-2 mt-1" />
          <p>
            <strong>Sustainability Spike</strong>
            <br />
            Gen Z favorite Reformation
            <br /> breaks into top 10.
          </p>
        </div>
        <div
          {...DEFAULT_SAL_PROPS}
          data-sal-delay={600}
          className="ml-4 mr-4 flex mb-4"
        >
          <IconArrow className="mr-2 mt-1" direction="down" fill={SPOT_CORAL} />
          <p>
            <strong>Fast Fashion Purge</strong>
            <br />
            Zara is down 35% losing
            <br /> 1,520 spots.
          </p>
        </div>
      </div>
      <p
        {...DEFAULT_SAL_PROPS}
        data-sal-delay={650}
        className="tup-source text-center mt-8 max-w-3xl m-auto"
      >
        Resale Value Ranking: thredUP evaluated 35,000+ unique brands on its
        platform and created an aggregate score to determine a brand&apos;s
        ranking based on demand, virality, and value to the seller. Jumps &
        Drops: These reflect changes in a brand&apos;s resale ranking between
        2018 and 2019.
      </p>
    </div>
  );
};

export { BestResaleValue };
